import { canonicalNikkeId, normalizeNikkeId } from '../../nikke-catalog/domain/nikkeCatalog.js';

export const RECOMMENDED_DECK_ELEMENTS = ['작열', '수냉', '전격', '풍압', '철갑'];
export const RECOMMENDED_DECK_FILTERS = ['전체', ...RECOMMENDED_DECK_ELEMENTS];

const ELEMENT_ALIASES = new Map([
  ['작열', '작열'], ['화염', '작열'], ['불', '작열'], ['fire', '작열'],
  ['수냉', '수냉'], ['수속', '수냉'], ['물', '수냉'], ['water', '수냉'],
  ['전격', '전격'], ['전기', '전격'], ['electric', '전격'], ['electronic', '전격'],
  ['풍압', '풍압'], ['풍속', '풍압'], ['바람', '풍압'], ['wind', '풍압'],
  ['철갑', '철갑'], ['철', '철갑'], ['iron', '철갑'],
]);

function text(value) {
  return String(value ?? '').trim();
}

function compactKey(value) {
  return text(value).toLocaleLowerCase('ko-KR').replace(/[\s_\-:·.()[\]{}]/g, '');
}

export function canonicalRecommendedDeckCode(value, catalog = null) {
  const raw = text(value);
  if (!raw) return '';

  const resolved = catalog ? canonicalNikkeId(catalog, raw) : '';
  if (resolved) return resolved;

  if (/^c\d{1,4}$/i.test(raw)) return normalizeNikkeId(raw);
  if (/^\d{1,3}$/.test(raw)) return normalizeNikkeId(raw);
  return '';
}

export function canonicalizeRecommendedDeckCodes(value = [], catalog = null) {
  return normalizeRecommendedDeckCodes(value).map((code) => canonicalRecommendedDeckCode(code, catalog));
}

function elementOrder(value) {
  const index = RECOMMENDED_DECK_ELEMENTS.indexOf(normalizeRecommendedDeckElement(value));
  return index >= 0 ? index : RECOMMENDED_DECK_ELEMENTS.length;
}

export function normalizeRecommendedDeckElement(value) {
  const raw = text(value);
  if (!raw) return '';
  return ELEMENT_ALIASES.get(compactKey(raw)) || raw;
}

export function normalizeRecommendedDeckCodes(value = []) {
  return (Array.isArray(value) ? value : [])
    .map((item) => {
      if (typeof item === 'string' || typeof item === 'number') return text(item);
      return text(item?.code ?? item?.nikke_id ?? item?.nikkeId ?? item?.id);
    })
    .filter(Boolean)
    .slice(0, 5);
}

export function normalizeRecommendedDeck(raw = {}, bossElementByNo = new Map()) {
  const bossNo = Number(raw.boss_no ?? raw.bossNo ?? raw.target_boss_no ?? raw.targetBossNo ?? 0);
  const fallbackElement = Number.isInteger(bossNo) ? bossElementByNo.get(bossNo) : '';
  const element = normalizeRecommendedDeckElement(raw.element ?? fallbackElement);
  return {
    id: text(raw.id),
    unionId: text(raw.union_id ?? raw.unionId),
    title: text(raw.title) || '이름 없는 추천덱',
    element: element || '미지정',
    bossNo: Number.isInteger(bossNo) && bossNo > 0 ? bossNo : null,
    bossName: text(raw.boss_name ?? raw.bossName ?? raw.target_boss_name ?? raw.targetBossName),
    deck: normalizeRecommendedDeckCodes(raw.deck_codes ?? raw.deckCodes ?? raw.deck),
    note: text(raw.note),
    createdAt: raw.created_at ?? raw.createdAt ?? null,
    updatedAt: raw.updated_at ?? raw.updatedAt ?? null,
  };
}

export function recommendedDecksForBoss(decks = [], boss = {}) {
  const bossNo = Number(boss?.bossNo ?? boss?.boss_no ?? 0);
  const bossNameKey = compactKey(boss?.bossName ?? boss?.boss_name ?? boss?.name);
  const bossElement = normalizeRecommendedDeckElement(boss?.element);

  return (Array.isArray(decks) ? decks : [])
    .filter((deck) => {
      const deckBossNo = Number(deck?.bossNo ?? deck?.boss_no ?? 0);
      if (Number.isInteger(deckBossNo) && deckBossNo > 0 && Number.isInteger(bossNo) && bossNo > 0) {
        return deckBossNo === bossNo;
      }

      const deckBossNameKey = compactKey(deck?.bossName ?? deck?.boss_name ?? deck?.targetBossName);
      if (deckBossNameKey && bossNameKey) return deckBossNameKey === bossNameKey;

      return Boolean(bossElement) && normalizeRecommendedDeckElement(deck?.element) === bossElement;
    })
    .sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), 'ko-KR'));
}

export function validateRecommendedDeck(input = {}, catalog = null) {
  const title = text(input.title);
  const element = normalizeRecommendedDeckElement(input.element);
  const rawDeck = normalizeRecommendedDeckCodes(input.deck);
  if (!title) return { ok: false, message: '추천덱 이름을 입력해주세요.' };
  if (title.length > 100) return { ok: false, message: '추천덱 이름은 100자 이내로 입력해주세요.' };
  if (!RECOMMENDED_DECK_ELEMENTS.includes(element)) return { ok: false, message: '추천 속성을 선택해주세요.' };
  if (rawDeck.length !== 5) return { ok: false, message: '추천덱은 니케 5명을 모두 선택해야 합니다.' };

  const deck = canonicalizeRecommendedDeckCodes(rawDeck, catalog);
  if (deck.some((code) => !code)) {
    return { ok: false, message: '추천덱 니케 식별자를 확인할 수 없습니다. 니케를 다시 선택해주세요.' };
  }
  if (new Set(deck).size !== deck.length) return { ok: false, message: '추천덱 안에서 같은 니케를 중복 선택할 수 없습니다.' };
  if (text(input.note).length > 500) return { ok: false, message: '메모는 500자 이내로 입력해주세요.' };
  return { ok: true, value: { title, element, deck, note: text(input.note) } };
}

export function filterRecommendedDecks(decks = [], element = '전체', query = '') {
  const normalizedFilter = element === '전체' ? '전체' : normalizeRecommendedDeckElement(element);
  const needle = compactKey(query);
  return (Array.isArray(decks) ? decks : [])
    .filter((deck) => normalizedFilter === '전체' || normalizeRecommendedDeckElement(deck.element) === normalizedFilter)
    .filter((deck) => {
      if (!needle) return true;
      return [deck.title, deck.element, deck.bossName, deck.note]
        .map(compactKey)
        .some((value) => value.includes(needle));
    })
    .sort((a, b) => elementOrder(a.element) - elementOrder(b.element)
      || String(a.title || '').localeCompare(String(b.title || ''), 'ko-KR'));
}
